import React, { useState } from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Box, Button, Divider, Flex, Image, Text
} from 'components/ui';
import {
    colors, fontSizes, radii, shadows, space, breakpoints
} from 'style/config';
import Location from 'utils/Location';
import { ensureFreshReferrer } from 'ai/analytics/utils/superChatAnalyticsContext';
import { REWARD_TYPES, REWARD_FALLBACK_URLS } from 'ai/constants/superchat';

function RewardCard({
    products = [], rewardType = REWARD_TYPES.REWARDS_BAZAAR, viewOnly = false, localization, addRewardToBasket, closeSuperChat
}) {
    const [addedToBasket, setAddedToBasket] = useState({});
    const fallbackUrl = REWARD_FALLBACK_URLS[rewardType];

    const getPriceText = product => {
        if (product.is_free || rewardType === REWARD_TYPES.BIRTHDAY) {
            return (
                <span>
                    {localization.free}
                    <sup>∫</sup>
                </span>
            );
        }

        const points = product.reward_points || product.rewardPoints;

        return points ? `${points} ${localization.points}` : '';
    };

    const handleAddToBasket = async (e, product) => {
        e.stopPropagation();

        if (viewOnly) {
            Location.navigateTo(e, fallbackUrl);

            return;
        }

        const productKey = product.skuId || product.default_sku;

        try {
            ensureFreshReferrer();
            await addRewardToBasket(product.skuId || product.default_sku, product.productId || product.id);

            setAddedToBasket(prev => ({ ...prev, [productKey]: true }));
            setTimeout(() => {
                setAddedToBasket(prev => ({ ...prev, [productKey]: false }));
            }, 1000);
        } catch (error) {
            Sephora.logger.error(`Failed to add ${rewardType} reward to basket:`, error);
        }
    };

    const openProductPage = (event, product) => {
        const url = product.url || product.targetUrl;

        if (!url) {
            return;
        }

        const isMobile = window.matchMedia(breakpoints.smMax).matches;
        ensureFreshReferrer();
        Location.navigateTo(event, url);

        if (isMobile) {
            closeSuperChat();
        }
    };

    if (!products || products.length === 0) {
        return null;
    }

    return (
        <Box css={styles.container}>
            {products.map((product, index) => (
                <Box
                    is='div'
                    key={product.skuId || product.default_sku || index}
                    marginBottom={space[4]}
                    onClick={e => openProductPage(e, product)}
                    css={{ cursor: 'pointer' }}
                >
                    <Flex
                        alignItems='flex-start'
                        css={styles.productContent}
                    >
                        {(product.image_url || product.imageUrl) && (
                            <Box css={styles.imageContainer}>
                                <Image
                                    src={product.image_url || product.imageUrl}
                                    alt={product.title || product.brand_name || ''}
                                    css={styles.productImage}
                                />
                            </Box>
                        )}
                        <Flex
                            flexDirection='column'
                            flex='1'
                            css={styles.productDetails}
                        >
                            <Box>
                                <Text
                                    display='block'
                                    fontSize='sm'
                                    marginBottom='.125em'
                                    fontWeight='bold'
                                    numberOfLines={1}
                                >
                                    {product.brand_name || product.brandName}
                                </Text>
                                {(product.title || product.displayName) && (
                                    <Text
                                        fontSize='sm'
                                        numberOfLines={2}
                                    >
                                        {product.title || product.displayName}
                                    </Text>
                                )}
                            </Box>
                            <Text
                                fontWeight='bold'
                                css={styles.priceText}
                            >
                                {getPriceText(product)}
                            </Text>
                            <Box marginTop='auto'>
                                <Button
                                    variant={viewOnly ? 'secondary' : 'special'}
                                    size='sm'
                                    css={styles.addToBasketButton}
                                    onClick={e => handleAddToBasket(e, product)}
                                >
                                    {addedToBasket[product.skuId || product.default_sku] ? localization.added : localization.addToBasket}
                                </Button>
                            </Box>
                        </Flex>
                    </Flex>
                    {index < products.length - 1 && (
                        <Divider
                            marginTop={20}
                            marginBottom={20}
                        />
                    )}
                </Box>
            ))}
        </Box>
    );
}

const styles = {
    container: {
        backgroundColor: colors.white,
        borderRadius: 12,
        padding: space[3],
        boxShadow: shadows.light,
        marginBottom: space[2]
    },
    productContent: {
        gap: space[3]
    },
    imageContainer: {
        flexShrink: 0
    },
    productImage: {
        width: 120,
        height: 120,
        objectFit: 'cover',
        borderRadius: `${radii[1]}px`
    },
    productDetails: {
        gap: 0
    },
    priceText: {
        marginTop: space[1],
        marginBottom: space[1]
    },
    addToBasketButton: {
        width: 155,
        fontSize: fontSizes.sm,
        paddingTop: space[2],
        paddingBottom: space[2]
    }
};

export default wrapFunctionalComponent(RewardCard, 'RewardCard');
